import React from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import { missionLabel, QC_DOC_TYPES } from "@/lib/labels";

const fetcher = (u) => api.get(u).then((r) => r.data);

/** Soumission d'un document pour relecture : livrable IA, fichier déposé ou texte collé. */
export function QcCreateDialog({ open, setOpen, onCreated }) {
  const { data: missions = [] } = useSWR("/missions", fetcher);
  const { data: deliverables = [] } = useSWR("/deliverables", fetcher);
  const [form, setForm] = React.useState({ mission_id: "", title: "", doc_type: "rapport_audit" });
  const [source, setSource] = React.useState("livrable");
  const [deliverableId, setDeliverableId] = React.useState("");
  const [contentText, setContentText] = React.useState("");
  const [file, setFile] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const readyDeliverables = deliverables.filter((d) => d.status === "pret");
  const canSubmit = !!form.mission_id && !!form.title.trim() && (
    (source === "livrable" && !!deliverableId) ||
    (source === "texte" && !!contentText.trim()) ||
    (source === "fichier" && !!file)
  );

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let r;
      if (source === "fichier") {
        const fd = new FormData();
        fd.append("mission_id", form.mission_id);
        fd.append("title", form.title.trim());
        fd.append("doc_type", form.doc_type);
        fd.append("file", file);
        r = await api.post("/qc/reviews/upload", fd);
      } else {
        r = await api.post("/qc/reviews", {
          mission_id: form.mission_id,
          title: form.title.trim(),
          doc_type: form.doc_type,
          deliverable_id: source === "livrable" ? deliverableId : null,
          content_text: source === "texte" ? contentText : null,
        });
      }
      toast.success("Document soumis pour relecture");
      setOpen(false);
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Soumission impossible");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="qc-new-btn" className="bg-[#2E3192] hover:bg-[#1E2066]"><Plus size={16} className="mr-1" /> Soumettre un document</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Soumettre pour relecture</DialogTitle>
          <DialogDescription>Rattachez le document à une mission, puis choisissez la source : livrable généré par l'IA, fichier déposé ou texte collé.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Mission *</Label>
            <Select value={form.mission_id} onValueChange={(v) => setForm({ ...form, mission_id: v })}>
              <SelectTrigger data-testid="qc-mission-select"><SelectValue placeholder="Sélectionner une mission" /></SelectTrigger>
              <SelectContent>
                {missions.map((m) => (<SelectItem key={m.id} value={m.id}>{m.title} — {missionLabel(m.mission_type)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Titre du document *</Label>
              <Input data-testid="qc-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Rapport d'audit 2025 — Client X" />
            </div>
            <div>
              <Label>Type de document</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger data-testid="qc-doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {QC_DOC_TYPES.map((d) => (<SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Tabs value={source} onValueChange={setSource}>
            <TabsList>
              <TabsTrigger value="livrable" data-testid="qc-source-livrable">Livrable IA</TabsTrigger>
              <TabsTrigger value="fichier" data-testid="qc-source-fichier">Fichier</TabsTrigger>
              <TabsTrigger value="texte" data-testid="qc-source-texte">Texte</TabsTrigger>
            </TabsList>
            <TabsContent value="livrable">
              <Label>Livrable généré</Label>
              <Select value={deliverableId} onValueChange={setDeliverableId}>
                <SelectTrigger data-testid="qc-deliverable-select"><SelectValue placeholder="Sélectionner un livrable prêt" /></SelectTrigger>
                <SelectContent>
                  {readyDeliverables.map((d) => (<SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>))}
                </SelectContent>
              </Select>
              {readyDeliverables.length === 0 && <p className="text-xs text-zinc-500 mt-1">Aucun livrable prêt — utilisez l'onglet Fichier ou Texte.</p>}
            </TabsContent>
            <TabsContent value="fichier">
              <Label>Fichier (.pdf, .docx, .txt — 20 Mo max)</Label>
              <Input data-testid="qc-file-input" type="file" accept=".pdf,.docx,.txt,.md,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <p className="text-xs text-zinc-500 mt-1">Le texte est extrait automatiquement pour la relecture IA.</p>
            </TabsContent>
            <TabsContent value="texte">
              <Label>Coller le contenu du document</Label>
              <Textarea data-testid="qc-content-textarea" rows={8} value={contentText} onChange={(e) => setContentText(e.target.value)} placeholder="Collez ici le texte du rapport à relire…" />
            </TabsContent>
          </Tabs>

          <div className="flex justify-end">
            <Button type="submit" disabled={!canSubmit || submitting} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="qc-submit">
              {submitting ? "Envoi en cours…" : "Soumettre pour relecture"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
